<template>
  <div class="min-h-screen">
    <!-- SEO Meta Tags -->
    <PageSEO
      title="Mobile App Development Services - React Native, iOS & Android"
      description="Cross-platform and native mobile app development for iOS and Android. We build fast, polished apps with React Native and publish them to the App Store and Google Play."
      :keywords="['mobile app development', 'React Native', 'iOS development', 'Android development', 'cross-platform apps', 'App Store', 'Google Play']"
      url="https://vedware.com/services/mobile-app-development"
    />

    <!-- Hero Section -->
    <HeroSection
      :icon="PhDeviceMobile"
      title="Mobile App"
      highlighted-title="Development"
      description="Cross-platform and native mobile apps for iOS and Android. We build fast, polished apps with React Native and take them all the way to the App Store and Google Play."
    />

    <!-- Service Overview -->
    <ServiceOverview
      :description="[
        'Our mobile app development service delivers production-ready iOS and Android apps from a single, well-structured codebase. With React Native we ship to both platforms faster, while still reaching for native code where performance or platform features demand it.',
        'We handle the whole lifecycle: product discovery, UX and UI, development, backend and API integration, testing, store submission, and ongoing maintenance and updates.',
      ]"
      :features="[
        'Cross-platform apps with React Native',
        'Native iOS and Android development',
        'Backend and API integration',
        'Push notifications and deep linking',
        'Offline support and secure local storage',
        'App Store and Google Play publishing',
      ]"
    />

    <!-- Technologies Section -->
    <ServiceTechnologies
      title="Mobile Technologies"
      description="We use proven mobile technologies and tooling to build apps that feel native and scale with your product."
      :categories="technologyCategories"
    />

    <!-- Services Offered -->
    <ServiceOfferings
      title="Mobile Services"
      description="From the first prototype to store release and beyond, we cover everything your mobile product needs."
      :services="mobileServices"
    />

    <ServiceProjects slug="mobile-app-development" />

    <!-- CTA Section -->
    <ServiceCTA
      title="Ready to Build Your"
      highlighted-title="Mobile App?"
      description="Let's turn your idea into an app your users will love on both iOS and Android."
    />
  </div>
</template>

<script setup lang="ts">
import {
  PhAndroidLogo,
  PhAppleLogo,
  PhBell,
  PhCloud,
  PhDeviceMobile,
  PhStorefront,
} from '@phosphor-icons/vue'
import ServiceCTA from '~/components/Services/ServiceCTA.vue'
import ServiceOfferings from '~/components/Services/ServiceOfferings.vue'
import ServiceOverview from '~/components/Services/ServiceOverview.vue'
import ServiceProjects from '~/components/Services/ServiceProjects.vue'
import ServiceTechnologies from '~/components/Services/ServiceTechnologies.vue'
import HeroSection from '~/components/Shared/HeroSection.vue'
import PageSEO from '~/components/Shared/PageSEO.vue'

const technologyCategories = [
  {
    title: 'Cross-platform',
    icon: PhDeviceMobile,
    technologies: [
      'React Native',
      'Expo',
      'TypeScript',
      'React Navigation',
      'Redux',
      'Reanimated',
      'Native Modules',
      'Hermes',
    ],
  },
  {
    title: 'Native Platforms',
    icon: PhAppleLogo,
    technologies: [
      'iOS',
      'Android',
      'Swift',
      'Kotlin',
      'Xcode',
      'Android Studio',
      'TestFlight',
      'Firebase App Distribution',
    ],
  },
  {
    title: 'Services & Release',
    icon: PhCloud,
    technologies: [
      'REST APIs',
      'GraphQL',
      'Push Notifications',
      'Firebase',
      'In-App Purchases',
      'Fastlane',
      'CI/CD Pipelines',
      'App Store Connect',
    ],
  },
]

const mobileServices = [
  {
    icon: PhDeviceMobile,
    title: 'React Native Apps',
    description: 'One codebase for iOS and Android with near-native performance, shorter delivery time, and lower cost of ownership.',
  },
  {
    icon: PhAppleLogo,
    title: 'iOS Development',
    description: 'Apps that follow Apple design guidelines and take advantage of platform features, built with React Native or Swift.',
  },
  {
    icon: PhAndroidLogo,
    title: 'Android Development',
    description: 'Reliable Android apps that run well across a wide range of devices and screen sizes, built with React Native or Kotlin.',
  },
  {
    icon: PhCloud,
    title: 'Backend & API Integration',
    description: 'Secure connections to your backend, third-party services, and payment providers, with a mobile-friendly API where needed.',
  },
  {
    icon: PhBell,
    title: 'Push Notifications & Engagement',
    description: 'Push notifications, deep links, and in-app messaging to keep users coming back.',
  },
  {
    icon: PhStorefront,
    title: 'Store Publishing & Updates',
    description: 'App Store and Google Play submission, over-the-air updates, monitoring, and long-term maintenance.',
  },
]
</script>
